// ============================================================================
// PostingOverviewCard — carte affichage liste (GEST CARTES METIER)
// ============================================================================

import { Paperclip } from 'lucide-react';
import { useTranslation } from '../../i18n';
import type { PostingSummary } from '../../hooks/usePostings';
import { PostingStatusBadge } from '../../pages/affichages/PostingStatusBadge';
import { formatDate, formatDateOnly, isDeadlineSoon } from '../../pages/affichages/format';
import {
  BusinessEntityCard,
  BusinessEntityCardMetaItem,
  BusinessEntityCardMetaRow,
} from '../BusinessEntityCard';

export interface PostingOverviewCardProps {
  posting: PostingSummary;
  onOpen: (postingId: string) => void;
}

export function PostingOverviewCard({ posting, onOpen }: PostingOverviewCardProps) {
  const { t, i18n } = useTranslation();

  return (
    <BusinessEntityCard
      title={
        <span className="inline-flex min-w-0 items-start gap-1.5">
          <span className="truncate">{posting.title}</span>
          {posting.hasAttachment ? (
            <Paperclip
              size={14}
              aria-label={t('gest:affichages.attachment.present')}
              className="mt-0.5 shrink-0 text-slate-400"
            />
          ) : null}
        </span>
      }
      subtitle={posting.teamName ?? t('gest:affichages.noTeam')}
      badge={<PostingStatusBadge status={posting.status} />}
      activateLabel={t('gest:affichages.cards.openPosting', { title: posting.title })}
      onActivate={() => onOpen(posting.id)}
      meta={
        <BusinessEntityCardMetaRow>
          <BusinessEntityCardMetaItem label={t('gest:affichages.columns.postedAt')}>
            {formatDate(i18n.language, posting.postedAt)}
          </BusinessEntityCardMetaItem>
          <BusinessEntityCardMetaItem label={t('gest:affichages.columns.deadline')}>
            {posting.deadline ? (
              <span
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-chip font-medium ${
                  isDeadlineSoon(posting.deadline)
                    ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                    : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                }`}
              >
                {formatDateOnly(i18n.language, posting.deadline)}
              </span>
            ) : (
              <span className="font-medium text-slate-400 dark:text-slate-500">—</span>
            )}
          </BusinessEntityCardMetaItem>
        </BusinessEntityCardMetaRow>
      }
    />
  );
}
